import { useState } from "react";
import { AgentEffect } from "@/common/components/AgentEffect";
import { resolveImageUrl } from "../resolveImageUrl";
import { pickRowPhotoUrl } from "../lib/placeFields";
import {
  normalizePlaceAddress,
  formatShortAddress,
} from "../lib/placeAddress";
import { placeBelongsToModule } from "../lib/placeMembership";
import type { PlaceObject } from "../types";

interface PlaceFolder {
  id: string;
  label: string;
  objectCount: number | undefined;
}

interface FolderMenu {
  id: string;
  name: string;
  x: number;
  y: number;
}

interface PlaceFolderGridProps {
  placeFolders: PlaceFolder[];
  allPlaces: any[];
  groupedFolders: unknown[];
  folderSearch: string;
  renamingFolderId: string | null;
  renameFolderText: string;
  renameFolderInputRef: React.RefObject<HTMLInputElement | null>;
  folderMenu: FolderMenu | null;
  setCurrentFolder: (v: { id: string; label: string } | null) => void;
  setRenameFolderText: (v: string) => void;
  setRenamingFolderId: (v: string | null) => void;
  setFolderMenu: (v: FolderMenu | null) => void;
  handleRenameFolder: (id: string, text: string) => void;
  handlePlaceClick: (place: PlaceObject) => void;
}

/**
 * The Collections-tab folder list + the "matching places" rows shown while
 * searching collections. State stays in the orchestrator and is threaded
 * through props.
 */

const PlacePhotoPlaceholder = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
    stroke="var(--color-text-muted, #bbb)" strokeWidth="1.5">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="m21 15-5-5L5 21" />
  </svg>
);

function PlacePhoto({ url }: { url: string }) {
  const [errored, setErrored] = useState(false);
  if (errored) return <PlacePhotoPlaceholder />;
  return (
    <img
      src={url}
      alt=""
      style={{ width: "100%", height: "100%", objectFit: "cover" }}
      onError={() => setErrored(true)}
    />
  );
}

export function PlaceFolderGrid({
  placeFolders,
  allPlaces,
  groupedFolders,
  folderSearch,
  renamingFolderId,
  renameFolderText,
  renameFolderInputRef,
  folderMenu,
  setCurrentFolder,
  setRenameFolderText,
  setRenamingFolderId,
  setFolderMenu,
  handleRenameFolder,
  handlePlaceClick,
}: PlaceFolderGridProps) {
  return (
    <>
      {placeFolders.map((folder) => {
        const folderPlaces = allPlaces.filter((p: any) =>
          placeBelongsToModule(p, folder.id, {
            includeModuleLinks: true,
          }),
        );
        const preview =
          folderPlaces
            .slice(0, 3)
            .map((p: any) => p.title || p.name || "")
            .filter(Boolean)
            .join(" · ") + (folderPlaces.length > 3 ? "…" : "");
        return (
          <div
            key={folder.id}
            onClick={() => {
              if (renamingFolderId === folder.id) return;
              setCurrentFolder({ id: folder.id, label: folder.label });
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 16px",
              cursor: "pointer",
              transition: "background 0.1s",
              borderBottom:
                "1px solid var(--color-border-light, rgba(0,0,0,0.04))",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.background =
                "var(--color-surface-hover)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.background =
                "transparent";
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#1D4ED810",
                flexShrink: 0,
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1D4ED8"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              {renamingFolderId === folder.id ? (
                <input
                  ref={renameFolderInputRef}
                  value={renameFolderText}
                  onChange={(e) => setRenameFolderText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter")
                      handleRenameFolder(folder.id, renameFolderText);
                    if (e.key === "Escape") setRenamingFolderId(null);
                  }}
                  onBlur={() =>
                    handleRenameFolder(folder.id, renameFolderText)
                  }
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: "100%",
                    border: "none",
                    outline: "none",
                    fontSize: 14,
                    fontWeight: 500,
                    fontFamily: "'DM Sans', sans-serif",
                    background: "transparent",
                    color: "var(--color-text)",
                  }}
                />
              ) : (
                <>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "var(--color-text)",
                      fontFamily: "'DM Sans', sans-serif",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {folder.label}
                  </div>
                  {preview && (
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: "var(--color-text-muted, #999)",
                        marginTop: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {preview}
                    </div>
                  )}
                </>
              )}
            </div>

            <span
              style={{
                fontSize: 13,
                color: "var(--color-text-muted, #999)",
                fontFamily: "'DM Sans', sans-serif",
                flexShrink: 0,
              }}
            >
              {folderPlaces.length}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setFolderMenu(
                  folderMenu?.id === folder.id
                    ? null
                    : {
                        id: folder.id,
                        name: folder.label,
                        x: e.clientX,
                        y: e.clientY,
                      },
                );
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px 4px",
                color: "var(--color-text-muted, #bbb)",
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <circle cx="12" cy="5" r="2" />
                <circle cx="12" cy="12" r="2" />
                <circle cx="12" cy="19" r="2" />
              </svg>
            </button>
          </div>
        );
      })}

      {folderSearch &&
        (() => {
          const needle = folderSearch.toLowerCase();
          const matchingPlaces = allPlaces
            .filter((p: any) => {
              const haystack = [
                p.name,
                p.title,
                p.address,
                p.userNotes,
                p.description,
              ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
              return haystack.includes(needle);
            })
            .sort((a: any, b: any) => {
              const aR = a.userRating ?? 0;
              const bR = b.userRating ?? 0;
              return bR - aR;
            });
          if (
            matchingPlaces.length === 0 &&
            groupedFolders.length === 0
          ) {
            return (
              <div className="text-center text-gray-500 py-8">
                No matching collections or places
              </div>
            );
          }
          if (matchingPlaces.length === 0) return null;
          return (
            <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
              <div
                style={{
                  padding: "12px 16px 4px",
                  fontSize: 11,
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "var(--color-text-muted, #999)",
                }}
              >
                {matchingPlaces.length} place
                {matchingPlaces.length !== 1 ? "s" : ""}
              </div>
              {matchingPlaces.map((place: any) => {
                const photoUrl = resolveImageUrl(pickRowPhotoUrl(place));
                const address = normalizePlaceAddress(place);
                const userNotes = place.userNotes;
                const shortAddress = formatShortAddress(address);
                return (
                  <div
                    key={place.id}
                    onClick={() => handlePlaceClick(place)}
                    style={{
                      position: "relative",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 16,
                      padding: "14px 20px",
                      cursor: "pointer",
                      transition: "background 0.1s",
                      borderBottom:
                        "1px solid var(--color-border-light, rgba(0,0,0,0.06))",
                    }}
                    onMouseEnter={(e) => {
                      (
                        e.currentTarget as HTMLDivElement
                      ).style.background = "var(--color-surface-hover)";
                    }}
                    onMouseLeave={(e) => {
                      (
                        e.currentTarget as HTMLDivElement
                      ).style.background = "transparent";
                    }}
                  >
                    <AgentEffect resourceType="place" objectId={place.id} />
                    <div
                      style={{
                        width: 110,
                        height: 78,
                        borderRadius: 6,
                        overflow: "hidden",
                        flexShrink: 0,
                        background: "var(--color-surface-hover)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {photoUrl ? (
                        <PlacePhoto url={photoUrl} />
                      ) : (
                        <svg
                          width="28"
                          height="28"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="var(--color-text-muted, #bbb)"
                          strokeWidth="1.5"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="18"
                            height="18"
                            rx="2"
                          />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="m21 15-5-5L5 21" />
                        </svg>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 500,
                          color: "var(--color-text)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {place.title || place.name}
                      </div>
                      {shortAddress && (
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 400,
                            color: "var(--color-text-secondary, #888)",
                            marginTop: 3,
                          }}
                        >
                          {shortAddress}
                        </div>
                      )}
                      {userNotes && (
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 400,
                            color: "var(--color-text-secondary, #636871)",
                            marginTop: 4,
                            fontStyle: "italic",
                            lineHeight: 1.4,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical" as const,
                            overflow: "hidden",
                          }}
                        >
                          {userNotes}
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        flexShrink: 0,
                        textAlign: "center",
                        minWidth: 36,
                      }}
                    >
                      {place.userRating ? (
                        <>
                          <div
                            style={{
                              fontSize: 15,
                              fontWeight: 500,
                              color: "var(--color-text)",
                            }}
                          >
                            {place.userRating.toFixed(1)}
                          </div>
                          <div
                            style={{
                              width: 24,
                              height: 2,
                              background: "#1a9ba5",
                              borderRadius: 1,
                              margin: "3px auto",
                            }}
                          />
                        </>
                      ) : null}
                      {place.priceLevel && (
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 400,
                            color: "var(--color-text-muted, #999)",
                            marginTop: place.userRating ? 2 : 0,
                          }}
                        >
                          {place.priceLevel}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

    </>
  );
}
